import {
  Grid,
  Header,
  StatusIndicator,
  TextContent,
} from '@amzn/awsui-components-react';
import { EuiCard } from '@elastic/eui';
import { useDomainConfigQuery } from '../../DomainConfig/hooks/useDomainConfigQuery';
import { BestPracticesPanel } from '@amzn/search-services-cluster-insights-commons-lib'
import { useToastNotifications } from '../../../hooks/useToastList';
import { get } from 'lodash';
import { AnyObject } from '../../../models/types';
import { EuiPanel } from '@elastic/eui';
import { EuiTitle } from '@elastic/eui';
import { EuiFlexItem } from '@elastic/eui';
import { EuiFlexGrid } from '@elastic/eui';
import { EuiToolTip } from '@elastic/eui';
import { EuiBadge } from '@elastic/eui';
import { useEffect, useState } from 'react';
import { apiCall } from '../../../sdpds/utils';
import { InsightsMetadataResponse } from '../Constants/insightTypes';

interface ClusterHealth {
  status: 'green' | 'yellow' | 'red';
  active_shards: number;
  initializing_shards: number;
  unassigned_shards: number;
}

interface ClusterStats {
  indices: {
    count: number;  
    docs: {
      count: number; 
    };
  };
}

interface ClusterHealthResponse {
  clusterHealth: ClusterHealth;
  nodes: number;
  clusterStats: ClusterStats;
  workloadClassification: InsightsMetadataResponse;
}

interface ClusterHealthSummaryProps {
  getClusterHealthSummaryData?: ClusterHealthResponse;
  getClusterHealthSummaryLoading: boolean;
  domainIdent: string;
  bestPracticesPanelParameters: object;
  domainData: object;
}

const ClusterHealthSummary: React.FC<ClusterHealthSummaryProps> = ({ 
  getClusterHealthSummaryData, 
  getClusterHealthSummaryLoading,
  domainIdent,
  bestPracticesPanelParameters,
  domainData
}) => {
  const getStatusType = (status) => {
    switch (status?.toLowerCase()) {
      case 'green':
        return 'success';
      case 'yellow':
        return 'warning';
      case 'red':
        return 'error';
      default:
        return 'pending';
    }
  };

  const clusterHealth = getClusterHealthSummaryData?.clusterHealth;
  const totalShards = clusterHealth?.active_shards + clusterHealth?.initializing_shards + clusterHealth?.unassigned_shards;
  const totalIndices = getClusterHealthSummaryData?.clusterStats?.indices?.count;
  const totalDocuments = getClusterHealthSummaryData?.clusterStats?.indices?.docs?.count;
  const version = domainData?.domain?.rawConfig?.elasticsearchVersion?.elasticsearchVersion;
  const workloadClassification = getClusterHealthSummaryData?.workloadClassification?.insightsMetadata?.[0]?.usecases?.['Classification']
  const workloadClassificationType = workloadClassification?.type;

  const [updateDetails, setUpdateDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const renderLabelValue = (title: string, value: any) => {
    return (
      <EuiFlexItem>
        <div>
          <Header variant="h3">{title}</Header>
          <TextContent>
            {getClusterHealthSummaryLoading
              ? <StatusIndicator type="pending">Loading...</StatusIndicator>
              : value !== undefined
              ? value
              : 'N/A'}
          </TextContent>
        </div>
      </EuiFlexItem>
    )
  };

  const fetchUpdateDetails = async () => { 
    setIsLoading(true);
    try {
      const [stackName, accountId, clusterName] = domainIdent?.split('/');
      const response = await apiCall('/softwareUpdateDetails/status', {
        cluster: {
          stackName,
          clusterId: `${accountId}:${clusterName}`
        }
      });
      if (response.statusCode === 200) {
        setUpdateDetails(response.body);
        setIsError(false);
      } else {
        setIsError(true);
        setUpdateDetails(null);
      }
    } catch (error) {
      setIsError(true);
      setUpdateDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (domainIdent) {
      fetchUpdateDetails();
    }
  }, [domainIdent]);


  return (
    <EuiFlexGrid columns={2}>
      <EuiFlexItem>
      <EuiPanel paddingSize="l" hasShadow={false}>
        <EuiTitle size="s">
          <h3>Cluster Health Summary</h3>
        </EuiTitle>
        <EuiFlexGrid columns={3}>
          {renderLabelValue('Cluster Health', (<StatusIndicator type={getStatusType(clusterHealth?.status)}>{clusterHealth?.status || 'N/A'}</StatusIndicator>))}
          {renderLabelValue('Number of nodes', getClusterHealthSummaryData?.nodes)}
          {renderLabelValue('Number of shards', totalShards)}
          {renderLabelValue('Indices', totalIndices)}
          {renderLabelValue('Documents', totalDocuments)}
          {renderLabelValue('Version', version)}
          {renderLabelValue('Workload Classification', 
            workloadClassificationType ? (
              <EuiToolTip position="top" content={workloadClassification?.reason || 'No reason available'}>
                <EuiBadge color="yellow">{workloadClassificationType}</EuiBadge>
              </EuiToolTip>
            ) : (
              <EuiToolTip position="top" content="It may be because the domain doesn't have a user index yet, or the Beaglerock policy hasn't run on this domain. Please check again after some time.">
                <span>N/A</span>
              </EuiToolTip>
            )
          )}
          {renderLabelValue('Classification Confidence', 
            workloadClassification?.confidence !== undefined ? workloadClassification?.confidence : (
              <EuiToolTip position="top" content="It may be because the domain doesn't have a user index yet, or the Beaglerock policy hasn't run on this domain. Please check again after some time.">
                <span>N/A</span>
              </EuiToolTip>
            )
          )}
        </EuiFlexGrid>
      </EuiPanel>
      </EuiFlexItem>
      <EuiFlexItem>
      <BestPracticesPanel
        http={{}}
        notifications={{ toasts: useToastNotifications() }}
        domainName={domainIdent}
        fetchDomainDetails={() =>
          Promise.resolve({
            ...bestPracticesPanelParameters,
            ServiceSoftwareOptions: {
              UpdateAvailable: updateDetails?.isSoftwareUpdateAvailable,
              CurrentVersion: updateDetails?.currentClusterReleaseVersion,
              NewVersion: updateDetails?.availableStackReleaseSoftware?.releaseVersion,
            }
          })
        } 
      />
      </EuiFlexItem>
    </EuiFlexGrid>
  );
};

export { ClusterHealthSummary };